import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { GraduationCap, WarningCircle, CheckCircle } from '@phosphor-icons/react';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import securityService from '../../services/securityService';
import studentsService from '../../services/studentsService';
import './security.css';

export default function ParentQRPage() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const initialStudentId = searchParams.get('student');

  const [children, setChildren] = useState([]);
  const [selectedId, setSelectedId] = useState(initialStudentId || '');
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrLoading, setQrLoading] = useState(false);
  const [error, setError] = useState(null);

  const today = new Date().toLocaleDateString('en-GH', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
  const expiresAt = new Date();
  expiresAt.setHours(17, 0, 0, 0);

  // Load parent's children
  useEffect(() => {
    if (!user) return;
    studentsService.listStudents({ parent_id: user.id })
      .then(data => {
        const list = Array.isArray(data) ? data : (data?.students || []);
        setChildren(list);
        if (!selectedId && list.length > 0) setSelectedId(String(list[0].id));
      })
      .catch(() => setError('Could not load your children. Please try again.'))
      .finally(() => setLoading(false));
  }, [user, selectedId]);

  // Load QR token when student is selected
  useEffect(() => {
    if (!selectedId) return;
    setQrData(null);
    setQrLoading(true);
    securityService.getStudentQr(selectedId)
      .then(data => setQrData(data))
      .catch(err => {
        if (err.message?.includes('404') || err.status === 404) {
          setQrData({ notReady: true });
        } else {
          setError(`Could not load QR code: ${err.message}`);
        }
      })
      .finally(() => setQrLoading(false));
  }, [selectedId]);

  const selectedChild = children.find(c => String(c.id) === String(selectedId));

  return (
    <Layout>
      <div className="parent-qr-page">
        <div style={{ width: '100%', maxWidth: 500 }}>

          {/* Child selector */}
          {children.length > 1 && (
            <div style={{
              background: 'white', border: '1px solid #e5e7eb', borderRadius: 10,
              padding: 12, marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center',
            }}>
              <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
                Viewing QR for:
              </span>
              <select
                value={selectedId}
                onChange={e => setSelectedId(e.target.value)}
                style={{
                  flex: 1, background: 'white', border: '1px solid #d1fae5',
                  borderRadius: 6, color: '#111827', padding: '6px 10px',
                  fontSize: 12, outline: 'none',
                }}
              >
                {children.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.first_name} {c.last_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {loading && (
            <div style={{ textAlign: 'center', padding: 40, color: '#6b7280', fontSize: 13 }}>
              Loading...
            </div>
          )}

          {error && (
            <div style={{
              background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 10,
              padding: 14, fontSize: 13, color: '#dc2626', marginBottom: 16,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <WarningCircle size={16} /> {error}
            </div>
          )}

          {!loading && !error && selectedChild && (
            <div className="parent-qr-email-wrap">
              {/* Header */}
              <div className="parent-qr-email-header">
                <div className="parent-qr-school-badge"><GraduationCap size={20} /></div>
                <h1 className="parent-qr-school-name">Student Pickup QR Code</h1>
                <p className="parent-qr-email-subject">{today}</p>
              </div>

              {/* Body */}
              <div className="parent-qr-email-body">
                <p className="parent-qr-greeting">
                  Dear <strong>{user?.first_name} {user?.last_name}</strong>,
                </p>
                <p className="parent-qr-instructions">
                  Your child's secure pickup QR code is shown below.
                  Present this QR code — or forward it to the designated pickup person —
                  at the school gate. The security officer will scan it to authorize release.
                </p>

                {/* Student card */}
                <div className="parent-qr-student-card">
                  <div className="parent-qr-student-avatar">
                    {(selectedChild.first_name?.[0] || '') + (selectedChild.last_name?.[0] || '')}
                  </div>
                  <div>
                    <p className="parent-qr-student-name">
                      {selectedChild.first_name} {selectedChild.last_name}
                    </p>
                    <p className="parent-qr-student-class">
                      {selectedChild.class_name || selectedChild.grade || 'Class not set'}
                    </p>
                  </div>
                  {selectedChild.student_id && (
                    <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                      <div style={{ fontSize: 10, color: '#94a3b8' }}>Student ID</div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>
                        {selectedChild.student_id}
                      </div>
                    </div>
                  )}
                </div>

                {/* QR Code section */}
                <div className="parent-qr-code-section">
                  <p className="parent-qr-code-title">Your Secure Pickup Code</p>
                  {qrLoading && (
                    <div style={{ padding: 40, color: '#6b7280', fontSize: 13 }}>
                      Generating QR code...
                    </div>
                  )}
                  {!qrLoading && qrData?.notReady && (
                    <div style={{
                      padding: 24, background: '#fef3c7', border: '1px solid #fde68a',
                      borderRadius: 12, textAlign: 'center', color: '#92400e', fontSize: 13,
                    }}>
                      QR codes haven't been dispatched yet today. They will be sent automatically at closing time.
                    </div>
                  )}
                  {!qrLoading && qrData?.token && (
                    <>
                      <div className="parent-qr-code-img">
                        <QRCode value={qrData.token} size={180} level="H" />
                      </div>
                      <br />
                      <span className="parent-qr-valid">
                        <CheckCircle size={16} /> Valid today until {expiresAt.toLocaleTimeString('en-GH', {
                          hour: '2-digit', minute: '2-digit',
                        })}
                      </span>
                    </>
                  )}
                </div>

                <div className="parent-qr-warning">
                  <span><WarningCircle size={16} /></span>
                  <span>
                    This QR code is single-use and expires at the end of today.
                    Only forward it to the authorized pickup person.
                  </span>
                </div>

                <div style={{ fontSize: 13, color: '#374151' }}>
                  <strong>What happens when the QR is scanned?</strong>
                  <ol style={{ margin: '8px 0 0', paddingLeft: 20, lineHeight: 1.8 }}>
                    <li>Security officer scans the QR at the gate</li>
                    <li>System verifies authorization</li>
                    <li>Your child is released to the authorized person</li>
                    <li>The pickup person's <strong>live location is shared back to you</strong> until your child arrives home</li>
                  </ol>
                </div>
              </div>

              <div className="parent-qr-footer">
                <p style={{ margin: 0 }}>
                  This is an automated security notification from the school's student security system.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
